<script setup lang="ts">

import { useCommonStore } from '@/stores/modules/common'
import { useUserStore } from '@/stores/modules/user';
import type { EquipItemType } from '@/types/equip';
import type { CrystalItemType } from '@/types/mall';
import { formatNumberWithCommas, formatNumberWithWan } from '@/utils';
import { socketEmit, socketEmitAsync } from '@/utils/web-socket';
import { CirclePlus } from '@element-plus/icons-vue'
import { reactive, ref } from 'vue';
import { PvpCardCommodity } from '../services/enum';

import DialogDeckResult from './DialogDeckResult.vue';
import { countBy } from 'lodash';
import type { DeckResult } from '@/api/pvp-card/type';
import SoulCurrency from './SoulCurrency.vue';
import { usePvpCardProfileStore } from '../services/gameProfile';
import Card from './Card.vue';
import { usePvpCardGameStore } from '../services/gameService';
import type { CardBasic } from '@/types/pvp-card';
import type SoulCurrencyVue from './SoulCurrency.vue';

const commonStore = useCommonStore()
const userStore = useUserStore()
const pvpCardProfileStore = usePvpCardProfileStore()
const pvpCardGameStore = usePvpCardGameStore()
const show = ref(false)
function handleRechargeDuelDiamond() {
  if (!userStore.duelCrystal) {
    ElMessage.warning("魂玉不足，请先获取魂玉！")
    return
  }
  show.value = true
}
const buyCount = ref(1)
const DiamondPrice = 1
function handleBuyDiamond() {
  const totalPrice = DiamondPrice * buyCount.value
  if (userStore.duelCrystal < totalPrice) return ElMessage.warning('魂玉不足')
  const name = '魂晶'
  ElMessageBox.confirm(`确认购买道具【${name}】吗？本次购买花费<b>${DiamondPrice * buyCount.value}</b>魂玉`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync<{ itemList: EquipItemType[] }>({
        type: 'buyPvpCardCommodity',
        data: { id: 60001, count: buyCount.value },
      })
      userStore.duelCrystal -= totalPrice
      userStore.consumables.duelDiamond += totalPrice
      show.value = false
      // ElMessage.success(`购买成功，获得${buyCount.value}个${name}`)
    })
    .catch(() => { })
}
type PvpCardItemType = Omit<CrystalItemType, 'key'> & { key: string, count: number, cardId: number, weekMax: number, weekBuy: number }
const diamondList = ref<PvpCardItemType[]>([])
const crystalList = ref<PvpCardItemType[]>([])
const weekCardList = ref<{ info: PvpCardItemType, card: CardBasic }[]>([])

let getDataDay = new Date().toISOString().slice(0, 10)

async function getPvpCardStoreCommodity() {
  socketEmit<{
    pvpCardDiamondStoreCollection: PvpCardItemType[],
    pvpCardCrystalStoreCollection: PvpCardItemType[],
    pvpCardWeekCardStoreCollection: PvpCardItemType[]
  }>({
    type: 'getPvpCardStoreCollection',
    callBack: (res) => {
      diamondList.value = res.data.pvpCardDiamondStoreCollection
      crystalList.value = res.data.pvpCardCrystalStoreCollection.filter((item) => item.key !== 'duelDiamond')
      weekCardList.value = res.data.pvpCardWeekCardStoreCollection.map((item) => {
        return {
          info: item,
          card: pvpCardGameStore.getDetailById(item.cardId)
        }
      })
      getDataDay = new Date().toISOString().slice(0, 10)
    },
  })
}
getPvpCardStoreCommodity()
const labelEnum: Record<string, string> = {
  duelDiamond: '魂晶',
  duelCrystal: '魂玉',
  duelWeekCard: '魂尘'
}

const dialogDeckResultRef = ref<InstanceType<typeof DialogDeckResult>>()
function handleBuyCommodity(item: PvpCardItemType, consumType: string) {
  const { price, key, id, weekBuy, weekMax } = item
  if (weekMax) {
    if (weekBuy >= weekMax) return ElMessage.warning('本周已购买完')
  }
  let name = PvpCardCommodity[key]
  const totalPrice = price * 1
  if (consumType === 'duelDiamond') {
    if (userStore.consumables.duelDiamond < totalPrice) {
      if (userStore.duelCrystal) {
        ElMessageBox.confirm('魂晶不足，是否使用魂玉兑换？', '提示', {
          type: 'warning',
          confirmButtonClass: 'is-plain',
        }).then(() => {
          show.value = true
        }).catch(() => { })
      } else {
        ElMessage.warning(`${labelEnum[consumType]}不足`)
      }
      return
    }
  } else if (consumType === 'duelCrystal') {
    if (userStore.duelCrystal < totalPrice) return ElMessage.warning(`${labelEnum[consumType]}不足`)
  } else if (consumType === 'duelWeekCard') {
    // 如果商店数据不是最新的，刷新商店数据(防0点)
    if (new Date().toISOString().slice(0, 10) !== getDataDay) {
      getPvpCardStoreCommodity()
      return ElMessage.warning('已刷新商店，请重新购买。')
    }
    const item = weekCardList.value.find(({ info }) => info.id === id)!
    name = item.card.name + 'x' + item.info.count
    if (userStore.consumables.duelCoin < totalPrice) return ElMessage.warning(`${labelEnum[consumType]}不足`)
  }
  ElMessageBox.confirm(`确认购买道具【${name}】吗？本次购买花费<b>${price * 1}</b>${labelEnum[consumType]}`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const { coin,
        cardIds } = await socketEmitAsync<DeckResult>({
          type: 'buyPvpCardCommodity',
          data: { id, count: 1, consumType },
        })
      if (consumType === 'duelDiamond') {
        userStore.consumables.duelDiamond -= totalPrice
      } else if (consumType === 'duelCrystal') {
        userStore.duelCrystal -= totalPrice
      } else if (consumType === 'duelWeekCard') {
        userStore.consumables.duelCoin -= totalPrice
      }
      const commodity = [...diamondList.value, ...crystalList.value, ...weekCardList.value.map((item) => item.info)]!.find((item) => item.id === id)
      if (!commodity) return

      if (commodity.key.startsWith('mallCardPack')) {
        userStore.consumables.duelCoin += coin
        dialogDeckResultRef.value?.handleOpenResult({
          coin,
          cards: cardIds
        })
        pvpCardProfileStore.init()
      } else if (commodity.key.startsWith('weekCard')) {
        dialogDeckResultRef.value?.handleOpenResult({
          cards: cardIds
        })
        pvpCardProfileStore.init()
        getPvpCardStoreCommodity()
      }

    })
    .catch(() => { })
}
const curTab = ref('weekSale')
</script>

<template>
  <div class="mall-page">
    <slot name="top"></slot>
    <h2>对决商城</h2>
    <div class="common-coin-info">
      <div style="float:left;">
        <span>
          <SoulCurrency type="dust" size="18px" /> {{ formatNumberWithWan(userStore.consumables.duelCoin) }}
        </span>
        <span @click="handleRechargeDuelDiamond">
          <SoulCurrency type="quartz" size="18px" /> {{ formatNumberWithWan(userStore.consumables.duelDiamond) }}
        </span>
      </div>
      <span>
        <SoulCurrency type="jade" size="18px" /> {{ formatNumberWithWan(userStore.duelCrystal) }}
      </span>
    </div>
    <el-tabs v-model="curTab" stretch>
      <el-tab-pane label="每周特惠" name="weekSale" class="pane">
        <div style="height: 100%;overflow-y: auto;overflow-x: hidden;">
          <div class="border-wrap area-wrap">
            <div class="float-title ">
              <span class="text-dust">
                轮换卡牌
              </span>
            </div>
            <div style="font-size: 12px;padding: 0 8px 8px;">注意：以下卡牌每日轮换，但每个位置每周限购一份</div>
            <div class="card-grid">
              <template v-for="(item) of weekCardList">
                <div v-if="item" :class="{ hasBuy: item.info.weekMax <= item.info.weekBuy }" :key="item.info.id"
                  class="card-item" @click="handleBuyCommodity(item.info, 'duelWeekCard')">
                  <div class="card-info">
                    <Card :card="item.card" />
                    <div class="card-count">x{{ item.info.count }}</div>
                  </div>
                  <div class="card-price">
                    {{ formatNumberWithWan(item.info.price) }}
                    <SoulCurrency type="dust" size="18px" />
                  </div>
                </div>
                <div v-else></div>
              </template>
            </div>
          </div>
        </div>
      </el-tab-pane>
      <el-tab-pane label="卡包直购" name="cardPack" class="pane">
        <div style="height: 100%;overflow-y: auto;overflow-x: hidden;">
          <div class="border-wrap area-wrap">
            <div class="float-title ">
              <span class="text-quartz">
                魂晶专区
              </span>
            </div>
            <div class="commodity-list">
              <div v-for="item of diamondList" :key="item.id">
                <el-button size="large" type="primary" plain @click="handleBuyCommodity(item, 'duelDiamond')">
                  <div class="info">
                    <div class="name">
                      {{ PvpCardCommodity[item.key] }}
                    </div>
                    <div class="price">
                      {{ item.price }}
                      <SoulCurrency type="quartz" size="18px" />
                    </div>
                  </div>
                  <div class="desc">{{ item.intro }}</div>
                </el-button>
              </div>
            </div>
          </div>
          <div style="height: 30px;"></div>
          <div class="border-wrap area-wrap">
            <div class="float-title text-jade">
              <span class="text-jade">
                魂玉专区
              </span>
            </div>
            <div class="commodity-list">
              <div v-for="item of crystalList" :key="item.id">
                <el-button size="large" type="warning" plain @click="handleBuyCommodity(item, 'duelCrystal')">
                  <div class="info">
                    <div class="name">
                      {{ PvpCardCommodity[item.key] }}
                    </div>
                    <div class="price">
                      {{ item.price }}
                      <SoulCurrency type="jade" size="18px" />
                    </div>
                  </div>
                  <div class="desc">{{ item.intro }}</div>
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </el-tab-pane>

    </el-tabs>

    <!-- 充值钻石 -->
    <el-dialog v-model="show" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">购买魂晶</span>
      </h2>
      <p>
        购买数量：
        <el-input-number size="small" :min="1" :max="userStore.duelCrystal" v-model="buyCount" :precision="0"
          :disabled="!userStore.duelCrystal"></el-input-number>
      </p>
      <p>价格：{{ DiamondPrice * buyCount }}
        <SoulCurrency type="jade" size="18px" />
      </p>
      <template #footer>
        <el-button plain size="small" @click="handleBuyDiamond">
          购入
        </el-button>
      </template>
    </el-dialog>
    <!-- 礼包结果 -->
    <DialogDeckResult ref="dialogDeckResultRef" />
  </div>
</template>

<style lang="scss" scoped>
.mall-page {
  height: 100%;
  padding-top: 0;
  display: flex;
  flex-flow: column nowrap;
}

.el-tabs {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;

  :deep() {
    .el-tabs__content {
      flex: 1;

      .pane {
        height: 100%;
      }
    }
  }
}

.common-coin-info {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &>div {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
  }
}

.float-title {
  background-color: var(--backColor);
  display: inline-block;
  padding: 0 10px;
  position: absolute;
  top: 0px;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: bold;
  font-size: 18px;

  .text-quartz {
    // 渐变字体
    background: linear-gradient(90deg, #FFADF5 0%, #AF26FF 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .text-jade {
    background: linear-gradient(90deg, #FFFFFE 0%, #01BA53 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .text-dust {
    background: linear-gradient(90deg, #C9C9C9 0%, #79777B 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
}

.area-wrap {
  padding: 20px 10px 10px;
  position: relative;
  margin-top: 10px;
}

.commodity-list {
  display: flex;
  flex-direction: column;
  gap: 16px;

  button {
    display: block;
    width: 100%;
    height: 90px;

    :deep(span) {
      display: flex;
      flex-direction: column;
      gap: 12px;
      word-break: break-word;
      white-space: pre-wrap;
      justify-items: center;
      align-items: center;

      .info {
        display: flex;
        width: 100%;
        justify-content: space-between;
      }

      .desc {
        text-align: left;
      }

      .name {
        font-size: 18px;
        font-weight: bold;
      }

      .price {
        color: #cfd3dc;
      }
    }
  }

}

.card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  .card-item {
    padding: 4px;
    border-radius: 4px;
    box-shadow: 2px 2px 2px rgba(255, 255, 255, 0.4) inset, -2px -2px 2px rgba(100, 100, 100, 0.4) inset;
    background-image: linear-gradient(to top, #334 0%, #112 20%, #001 100%);
    cursor: pointer;

    &.hasBuy {
      cursor: not-allowed;
      position: relative;
      pointer-events: none;

      &::after {
        content: "✔";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        color: #fff;
        font-size: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }



  .card-info {
    position: relative;
  }

  .card-count {
    position: absolute;
    bottom: 4px;
    left: 0;
    text-align: center;
    color: white;
    font-size: 14px;
    font-weight: bold;
    width: 100%;
    text-shadow: 0 0 2px black;
  }

  .card-price {
    font-size: 12px;
    text-align: center;
    color: #cfd3dc;
    white-space: nowrap;
  }


}
</style>
